<script module lang="ts">
  import { getContext, setContext } from 'svelte';

  /**
   * card ありの書く課題と画面（routes/Lesson.svelte）をつなぐ口。LessonView を経ずに context で渡す。
   * - draftOf / ondraft: 書きかけ（progress.saveDraft / deleteDraft。progress-store.md の drafts）
   * - onhint: 採点前にヒントを開いたか（AnswerInput.hint）
   */
  export interface LessonWriteHooks {
    draftOf?: (cardId: string) => string | undefined;
    ondraft?: (card: WriteAcCard, code: string) => void;
    onhint?: (card: WriteAcCard, level: number) => void;
  }

  const HOOKS_KEY = Symbol('lesson-write-hooks');

  export function setLessonWriteHooks(hooks: LessonWriteHooks): void {
    setContext(HOOKS_KEY, hooks);
  }

  /** テストで render の context に渡す */
  export function lessonWriteHooksContext(hooks: LessonWriteHooks): Map<symbol, LessonWriteHooks> {
    return new Map([[HOOKS_KEY, hooks]]);
  }

  function getLessonWriteHooks(): LessonWriteHooks {
    const h: unknown = getContext(HOOKS_KEY);
    return typeof h === 'object' && h !== null ? (h as LessonWriteHooks) : {};
  }
</script>

<script lang="ts">
  /**
   * 書く課題（lesson-format.md §4.4、§7.3）。
   * - card あり: write-ac カードを CardView でそのまま出す。回答の記録は親（画面）がドリルと同じ規則で行う。
   * - card なし: スターターから書き、全ケースを 1 回の実行で判定する。Leitner には入れない。
   *   AC か「模範解答を見る」で模範解答を出し、レッスンの進捗に残す。
   */
  import { onDestroy } from 'svelte';
  import type { WriteAcCard } from '../../lib/cards';
  import type { JudgeResult } from '../../lib/judge';
  import CodeBlock from '../CodeBlock.svelte';
  import CodeEditor from '../CodeEditor.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import VerdictBadge from '../VerdictBadge.svelte';
  import { SUBMIT_CHORD_LABEL } from '../keyboard';
  import type { GradeResult } from '../types';
  import CardView from '../cards/CardView.svelte';
  import type { CardAnswer } from '../cards/answer';
  import { RunState } from '../cards/run-state.svelte';
  import RunStatus from '../cards/RunStatus.svelte';
  import { caseLabel, compileErrorLine, firstFailure, writeAcResult } from '../cards/write-ac';
  import { gradeLessonWrite } from './run-lesson';
  import type { WriteBlock } from './types';

  interface Props {
    block: WriteBlock;
    /** card あり のときのカード（見つからなければ undefined） */
    card?: WriteAcCard | undefined;
    /** AC か「模範解答を見る」になったとき */
    ondone?: ((id: string) => void) | undefined;
    /** card ありのカードを採点したとき */
    oncardgrade?:
      ((card: WriteAcCard, result: GradeResult, answer: CardAnswer) => void) | undefined;
  }

  let { block, card, ondone, oncardgrade }: Props = $props();

  const hooks = getLessonWriteHooks();
  // 書きかけは描画のはじめに 1 回だけ読む（WriteACCard も初期表示にしか使わない）
  // svelte-ignore state_referenced_locally
  const cardDraft = card ? hooks.draftOf?.(card.id) : undefined;

  const uid = $props.id();
  // svelte-ignore state_referenced_locally
  let code = $state('starter' in block ? block.starter : '');
  let judge = $state<JudgeResult | null>(null);
  let gradeResult = $state<GradeResult | null>(null);
  let showSolution = $state(false);
  let graded = $state<{ result: GradeResult; answer: CardAnswer } | null>(null);
  const run = new RunState();
  onDestroy(() => run.dispose());

  const failure = $derived(judge ? firstFailure(judge) : undefined);

  async function submit() {
    if (!('cases' in block) || run.running || code.trim() === '') return;
    const cases = block.cases;
    const snapshot = code;
    judge = null;
    gradeResult = null;
    const res = await run.start((hooks) => gradeLessonWrite(snapshot, cases, hooks));
    if (!res) return;
    judge = res;
    gradeResult = writeAcResult(res, cases);
    if (res.overall === 'AC') reveal();
  }

  function reveal() {
    showSolution = true;
    ondone?.(block.id);
  }

  // コードを直して採点し直したら、新しい結果で上書きする
  function onCardGrade(result: GradeResult, answer: CardAnswer) {
    graded = { result, answer };
    if (card) oncardgrade?.(card, result, answer);
    if (result.verdict === 'AC') ondone?.(block.id);
  }
</script>

{#if 'card' in block}
  {#if card}
    <div class="embedded" data-testid="write-card">
      <CardView
        {card}
        index={1}
        total={1}
        result={graded?.result}
        answer={graded?.answer}
        allowRegrade
        ongrade={onCardGrade}
        draft={cardDraft}
        ondraft={hooks.ondraft ? (code) => hooks.ondraft?.(card, code) : undefined}
        onhint={hooks.onhint ? (level) => hooks.onhint?.(card, level) : undefined}
      />
    </div>
  {:else}
    <p class="note" data-testid="write-card-missing">この課題のカードは見つかりませんでした。</p>
  {/if}
{:else}
  <div class="write" data-testid="write-inline">
    <div class="samples">
      {#each block.cases as c, i (i)}
        <div class="sample">
          <div>
            <h4 class="sub">入力例 {i + 1}</h4>
            <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
            <pre class="output" tabindex="0" aria-label="入力例 {i + 1}">{c.in.replace(/\n$/, '') ||
                '（なし）'}</pre>
          </div>
          <div>
            <h4 class="sub">出力例 {i + 1}</h4>
            <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
            <pre class="output" tabindex="0" aria-label="出力例 {i + 1}">{c.out.replace(
                /\n$/,
                '',
              )}</pre>
          </div>
        </div>
      {/each}
    </div>

    <CodeEditor
      bind:value={code}
      id="{uid}-editor"
      readonly={run.running}
      minLines={6}
      onsubmit={() => void submit()}
    />

    <div class="actions">
      <button
        class="btn btn-primary"
        type="button"
        data-testid="write-submit"
        disabled={run.running || code.trim() === ''}
        aria-busy={run.running ? 'true' : undefined}
        onclick={() => void submit()}
        >{run.running ? '判定中' : 'ジャッジ'}<kbd>{SUBMIT_CHORD_LABEL}</kbd></button
      >
      <span class="note">全 {block.cases.length} ケースで判定します。</span>
    </div>

    <RunStatus {run} busyLabel="判定中" onretry={() => void submit()} />

    <div role="status" aria-live="polite" class="feedback">
      {#if judge && gradeResult}
        <div class="verdict-line">
          <VerdictBadge verdict={gradeResult.verdict} />
          {#if gradeResult.message}<p>{gradeResult.message}</p>{/if}
        </div>
      {/if}
    </div>

    {#if judge}
      <ol class="cases" aria-label="ケースごとの判定" data-testid="write-case-verdicts">
        {#each judge.cases as c (c.index)}
          <li>
            <VerdictBadge verdict={c.verdict} size="sm" caseLabel={caseLabel([], c.index)} />
            <span>{caseLabel([], c.index)}</span>
          </li>
        {/each}
      </ol>
      {#if judge.overall === 'CE'}
        {@const line = compileErrorLine(judge.compileOutput)}
        <h4 class="sub">コンパイラの出力{line ? `（${line} 行目）` : ''}</h4>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <pre class="output" tabindex="0" aria-label="コンパイラの出力">{judge.compileOutput}</pre>
      {:else if failure?.verdict === 'WA'}
        <OutputPanel
          mode="diff"
          expected={block.cases[failure.index]?.out ?? ''}
          actual={failure.stdout}
          stderr={failure.stderr}
        />
      {:else if failure}
        <OutputPanel mode="plain" stdout={failure.stdout} stderr={failure.stderr} />
      {/if}
    {/if}

    {#if showSolution}
      <div class="solution" data-testid="write-solution">
        <h4 class="sub">模範解答</h4>
        <CodeBlock code={block.solution.replace(/\n$/, '')} label="模範解答" />
      </div>
    {:else}
      <div class="actions">
        <button
          class="btn btn-quiet btn-sm"
          type="button"
          data-testid="write-reveal"
          onclick={reveal}>模範解答を見る</button
        >
      </div>
    {/if}
  </div>
{/if}

<style>
  .write {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .samples {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .sample {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
    gap: var(--space-3);
  }
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .actions kbd {
    margin-left: var(--space-2);
  }
  .note {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .verdict-line {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .cases {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-4);
  }
  .cases li {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-sm);
  }
  .solution {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
</style>
